<template>
  <div class="payment-admin">
    <ManagementListPage title="Comprovantes de pagamento"
      subtitle="Localize, confira e analise os comprovantes enviados pela devedora."
      :result-text="`${total} comprovantes encontrados`" :breadcrumbs="breadcrumbs"
      :summary-cards="summaryCards" page-class="payment-proof-list"
      @summary-click="applySummary($event.key)">
      <template #actions>
        <v-btn outlined color="primary" elevation="0" @click="applySummary('pending')">
          <v-icon left small>mdi-clipboard-check-outline</v-icon>
          Analisar fila ({{ summary.in_review }})
        </v-btn>
        <v-btn outlined color="primary" elevation="0" @click="exportCsv">
          <v-icon left small>mdi-download-outline</v-icon>Exportar CSV
        </v-btn>
        <v-btn color="primary" elevation="0" @click="openCreateDialog">
          <v-icon left small>mdi-plus</v-icon>Informar comprovante
        </v-btn>
      </template>

      <template #filters>
          <div class="op-filter-toolbar">
            <v-text-field dense outlined hide-details clearable
              prepend-inner-icon="mdi-magnify" placeholder="Buscar por CPF/CNPJ, processo ou responsável"
              v-model="filter.query" @keyup.enter="load(true)" @click:clear="load(true)" />
            <v-btn outlined color="primary" elevation="0" @click="showFilters = !showFilters">
              <v-icon left small>mdi-tune-variant</v-icon>Filtros
              <span v-if="activeFilterCount" class="management-filter-count">{{ activeFilterCount }}</span>
              <v-icon right small>{{ showFilters ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
            <v-btn color="primary" elevation="0" @click="applyAdvancedFilters"><v-icon left small>mdi-magnify</v-icon>Buscar</v-btn>
          </div>
          <v-expand-transition>
            <div v-if="showFilters" class="op-filter-panel mt-3 pt-3">
              <div class="op-filter-panel-head">
                <div><strong>Filtros avançados</strong><span>Refine os comprovantes exibidos na listagem.</span></div>
              </div>
              <v-row dense class="op-filter-grid">
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Status</label><v-select v-model="filter.status" :items="dataset.statuses" item-value="id" item-text="name" placeholder="Todos os status" dense outlined hide-details clearable @change="filter.pending = false" /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>CPF/CNPJ do credor</label><v-text-field v-model="filter.creditor_registration" placeholder="Todos os credores" dense outlined hide-details clearable /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Número do processo</label><v-text-field v-model="filter.process_number" placeholder="Todos os processos" dense outlined hide-details clearable /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Responsável</label><v-text-field v-model="filter.payment_responsible" placeholder="Todos os responsáveis" dense outlined hide-details clearable /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>CPF/CNPJ do pagador</label><v-text-field v-model="filter.payer_registration" placeholder="Todos os pagadores" dense outlined hide-details clearable /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Tipo de pagador</label><v-select v-model="filter.payer_type" :items="dataset.payerTypes" item-value="id" item-text="name" placeholder="Todos os tipos" dense outlined hide-details clearable /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Pagamento por terceiro</label><v-select v-model="filter.is_third_party_payment" :items="dataset.yesNo" item-value="id" item-text="name" placeholder="Todas as situações" dense outlined hide-details clearable /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Tipo ou motivo</label><v-select v-model="filter.document_type" :items="dataset.documentTypes" item-value="id" item-text="name" placeholder="Todos os tipos" dense outlined hide-details clearable /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Enviado de</label><v-text-field v-model="filter.created_at_start" type="date" dense outlined hide-details /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Enviado até</label><v-text-field v-model="filter.created_at_end" type="date" dense outlined hide-details /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Pagamento de</label><v-text-field v-model="filter.payment_date_start" type="date" dense outlined hide-details /></div></v-col>
                <v-col cols="12" md="6" lg="3"><div class="op-field"><label>Pagamento até</label><v-text-field v-model="filter.payment_date_end" type="date" dense outlined hide-details /></div></v-col>
              </v-row>
            </div>
          </v-expand-transition>
          <div v-if="showFilters" class="op-filter-footer">
            <span>{{ activeFilterCount ? `${activeFilterCount} filtro(s) ativo(s)` : 'Nenhum filtro adicional aplicado' }}</span>
            <v-btn v-if="activeFilterCount" text small color="primary" @click="clearFilters">Limpar filtros</v-btn>
          </div>
      </template>
      <template #table>
            <v-alert v-if="loadError" type="error" outlined>
              Não foi possível carregar os comprovantes.
              <v-btn text color="error" @click="load">Tentar novamente</v-btn>
            </v-alert>
            <v-data-table :headers="headers" :items="items" :server-items-length="total"
              :options.sync="options" :loading="loading" :disable-sort="true"
              :footer-props="footerOptions" class="app-table management-list-table management-minimal-table"
              no-data-text="Nenhum comprovante encontrado" loading-text="Carregando dados...">
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small class="op-table-badge" :color="statusColor(item.status)" dark>{{ item.status | enum(dataset.statuses, "id", "name") }}</v-chip>
              </template>
              <template v-slot:[`item.creditor`]="{ item }">
                <div class="payment-admin__creditor">
                  <strong class="op-cell-primary">{{ item.creditor_name || item.name || "Credor" }}</strong>
                  <span class="op-cell-secondary">{{ formatRegistration(item.creditor_registration) }}</span>
                </div>
              </template>
              <template v-slot:[`item.document_type`]="{ item }">
                {{ item.document_type | enum(dataset.documentTypes, "id", "name") }}
              </template>
              <template v-slot:[`item.paid_amount`]="{ item }">
                {{ formatCurrency(item.paid_amount) }}
              </template>
              <template v-slot:[`item.submitted_at`]="{ item }">
                {{ item.submitted_at | defaultDate("DD/MM/YYYY HH:mm") }}
              </template>
              <template v-slot:[`item.payment_date`]="{ item }">
                {{ item.payment_date | defaultDate("DD/MM/YYYY") }}
              </template>
              <template v-slot:[`item.origin`]="{ item }">
                <v-chip v-if="item.submitted_by_client_user_id && item.payment_proof_import_id"
                  small color="info" dark>Devedora · lote</v-chip>
                <span v-else-if="item.submitted_by_client_user_id">Devedora · individual</span>
                <span v-else>Administração Judicial</span>
              </template>
              <template v-slot:[`item.has_inconsistency`]="{ item }">
                <v-icon v-if="item.has_inconsistency" color="warning">mdi-alert-circle-outline</v-icon>
                <span v-else>Não</span>
              </template>
              <template v-slot:[`item.is_third_party_payment`]="{ item }">
                <v-chip v-if="item.is_third_party_payment" small color="warning" dark>Terceiro</v-chip>
                <span v-else>Não</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon small class="op-row-action-button" @click="viewItem(item)"><v-icon small>mdi-eye-outline</v-icon></v-btn>
              </template>
              <template v-slot:no-data>
                <div class="payment-admin__empty">
                  <v-icon size="44" color="#98A2B3">mdi-file-search-outline</v-icon>
                  <strong>Nenhum comprovante encontrado</strong>
                  <span>Remova alguns filtros ou amplie o período da consulta.</span>
                </div>
              </template>
            </v-data-table>
      </template>
    </ManagementListPage>

    <v-dialog v-model="createDialog" max-width="980" persistent content-class="payment-proof-create-dialog">
      <v-card class="payment-proof-create-card">
        <v-card-title class="payment-proof-create-header">
          <div>
            <span>Informar comprovante de pagamento</span>
            <small>Registre os dados do pagamento e anexe o documento comprobatório.</small>
          </div>
          <v-btn icon small aria-label="Fechar" @click="closeCreateDialog"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text class="payment-proof-create-body">
          <v-form ref="createFormRef">
            <section class="payment-proof-form-section">
              <header class="payment-proof-section-header">
                <span><v-icon size="18">mdi-account-credit-card-outline</v-icon></span>
                <div><strong>Credor e pagamento</strong><small>Identifique o caso, o credor e os dados financeiros do comprovante.</small></div>
              </header>
              <v-row dense class="payment-proof-form-grid">
                <v-col cols="12" md="6"><div class="op-field"><label>Caso / recuperanda <em>*</em></label><v-autocomplete v-model="createForm.client_id" :items="clients" item-value="id" item-text="name" placeholder="Selecione o caso" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
                <v-col cols="12" md="6"><div class="op-field"><label>CPF/CNPJ do credor <em>*</em></label><v-text-field v-model="createForm.creditor_registration" placeholder="Informe o CPF ou CNPJ" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
                <v-col cols="12" md="4"><div class="op-field"><label>Valor pago <em>*</em></label><v-currency-field v-model="createForm.paid_amount" placeholder="R$ 0,00" :allow-negative="false" dense outlined hide-details="auto" :rules="[createRules.required, positiveAmountRule]" /></div></v-col>
                <v-col cols="12" md="4"><div class="op-field"><label>Data do pagamento <em>*</em></label><v-text-field v-model="createForm.payment_date" type="date" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
                <v-col cols="12" md="4"><div class="op-field"><label>Forma ou motivo <em>*</em></label><v-select v-model="createForm.payment_reason" :items="dataset.paymentReasons" item-value="id" item-text="name" placeholder="Selecione o motivo" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
              </v-row>
            </section>

            <section class="payment-proof-form-section">
              <header class="payment-proof-section-header">
                <span><v-icon size="18">mdi-account-cash-outline</v-icon></span>
                <div><strong>Responsável pelo pagamento</strong><small>Informe quem realizou o pagamento e sua relação com o crédito.</small></div>
              </header>
              <v-row dense class="payment-proof-form-grid">
                <v-col cols="12" md="4"><div class="op-field"><label>Tipo de pagador <em>*</em></label><v-select v-model="createForm.payer_type" :items="dataset.payerTypes" item-value="id" item-text="name" placeholder="Selecione o tipo" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
                <v-col cols="12" :md="createForm.payer_type === 'creditor' ? 8 : 4"><div class="op-field"><label>Responsável pelo pagamento <em>*</em></label><v-text-field v-model="createForm.payment_responsible" placeholder="Informe o nome do responsável" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
                <v-col v-if="createForm.payer_type !== 'creditor'" cols="12" md="4"><div class="op-field"><label>CPF/CNPJ do pagador <em>*</em></label><v-text-field v-model="createForm.payer_registration" placeholder="Informe o CPF ou CNPJ" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
              </v-row>
            </section>

            <section class="payment-proof-form-section">
              <header class="payment-proof-section-header">
                <span><v-icon size="18">mdi-file-document-outline</v-icon></span>
                <div><strong>Processo e documento</strong><small>Relacione o processo quando disponível e classifique o comprovante.</small></div>
              </header>
              <v-row dense class="payment-proof-form-grid">
                <v-col cols="12" md="6"><div class="op-field"><label>Número do processo <span>Opcional</span></label><v-text-field v-model="createForm.process_number" placeholder="Informe o processo relacionado" dense outlined hide-details="auto" /></div></v-col>
                <v-col cols="12" md="6"><div class="op-field"><label>Tipo de documento <em>*</em></label><v-select v-model="createForm.document_type" :items="dataset.documentTypes" item-value="id" item-text="name" placeholder="Selecione o documento" dense outlined hide-details="auto" :rules="[createRules.required]" /></div></v-col>
              </v-row>
            </section>

            <section class="payment-proof-form-section payment-proof-document-section">
              <header class="payment-proof-section-header">
                <span><v-icon size="18">mdi-paperclip</v-icon></span>
                <div><strong>Arquivo do comprovante</strong><small>Envie um PDF, PNG ou JPG com no máximo 20 MB.</small></div>
              </header>
              <input ref="createDocumentInput" class="payment-proof-native-file" type="file" accept="application/pdf,image/png,image/jpeg" @change="onCreateDocumentSelected" />
              <div v-if="!createForm.document" class="payment-proof-dropzone" :class="{ dragging: createDragActive }" role="button" tabindex="0"
                @click="openCreateDocumentPicker" @keydown.enter.prevent="openCreateDocumentPicker" @keydown.space.prevent="openCreateDocumentPicker"
                @dragenter.prevent="createDragActive = true" @dragover.prevent="createDragActive = true" @dragleave.prevent="createDragActive = false" @drop.prevent="onCreateDocumentDrop">
                <span class="payment-proof-dropzone-icon"><v-icon size="25">mdi-cloud-upload-outline</v-icon></span>
                <div><strong>Arraste o comprovante para esta área</strong><small>ou clique para selecionar o arquivo</small></div>
                <span class="payment-proof-file-rule">PDF, PNG ou JPG · máximo de 20 MB</span>
              </div>
              <div v-else class="payment-proof-selected-file">
                <span><v-icon size="22">mdi-file-check-outline</v-icon></span>
                <div><strong>{{ createForm.document.name }}</strong><small>{{ formatFileSize(createForm.document.size) }}</small></div>
                <v-btn icon small aria-label="Remover arquivo" @click="removeCreateDocument"><v-icon size="18">mdi-close</v-icon></v-btn>
              </div>
            </section>
          </v-form>
        </v-card-text>

        <v-card-actions class="payment-proof-create-actions">
          <v-spacer />
          <v-btn outlined color="primary" elevation="0" @click="closeCreateDialog">Cancelar</v-btn>
          <v-btn color="primary" elevation="0" :loading="createSaving" @click="submitCreateProof"><v-icon left small>mdi-content-save-outline</v-icon>Salvar comprovante</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-navigation-drawer v-model="detailsDrawer" fixed temporary right :width="drawerWidth"
      class="payment-admin__details-drawer">
      <div class="payment-admin__drawer-shell">
        <header class="payment-admin__drawer-title">
          <div>
            <div class="payment-admin__drawer-heading">
              <h2>Análise do comprovante</h2>
              <v-chip v-if="details.status" x-small :color="statusColor(details.status)" dark>
                {{ details.status | enum(dataset.statuses, "id", "name") }}
              </v-chip>
            </div>
            <p v-if="details.id">#CP-{{ String(details.id).padStart(6, "0") }} · enviado em {{ details.submitted_at | defaultDate("DD/MM/YYYY HH:mm") }}</p>
            <p v-else>Confira o envio sem perder os filtros da lista.</p>
          </div>
          <v-btn icon outlined class="payment-admin__close" @click="detailsDrawer = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <main class="payment-admin__drawer-body">
          <v-skeleton-loader v-if="loadingDetails" type="article, image, actions" />
          <div v-else class="payment-admin__review-layout">
            <section class="payment-admin__document-panel">
              <div class="payment-admin__panel-heading">
                <div>
                  <strong>Documento enviado</strong>
                  <span>{{ selectedDocument ? selectedDocument.document_name : "Nenhum documento" }}</span>
                </div>
                <v-btn v-if="selectedDocument" small outlined color="primary"
                  :href="selectedDocument.document_url" target="_blank">
                  <v-icon left small>mdi-download-outline</v-icon>Baixar
                </v-btn>
              </div>
              <div v-if="selectedDocument" class="payment-admin__preview">
                <img v-if="isImageDocument(selectedDocument)" :src="selectedDocument.document_url"
                  alt="Pré-visualização do comprovante" />
                <iframe v-else-if="isPdfDocument(selectedDocument)" :src="selectedDocument.document_url"
                  title="Pré-visualização do comprovante" />
                <div v-else class="payment-admin__preview-empty">Pré-visualização indisponível.</div>
              </div>
              <div class="payment-admin__documents">
                <button v-for="(document, index) in details.documents || []" :key="document.id || index"
                  type="button" :class="{ active: selectedDocumentIndex === index }"
                  @click="selectedDocumentIndex = index">
                  <v-icon color="primary">mdi-file-document-outline</v-icon>
                  <span>{{ document.document_name || `Documento ${index + 1}` }}</span>
                </button>
              </div>
            </section>

            <section class="payment-admin__information-panel">
              <v-alert v-if="details.has_inconsistency" type="warning" outlined dense>
                {{ details.inconsistency_reason || "Há uma informação que precisa ser conferida." }}
              </v-alert>
              <div class="payment-admin__detail-list">
                <div v-for="field in detailFields" :key="field.label">
                  <span>{{ field.label }}</span><strong>{{ field.value }}</strong>
                </div>
              </div>
              <v-textarea class="mt-4" outlined dense rows="2" label="Observação interna"
                v-model="analysisForm.internal_observation" />
              <v-textarea outlined dense rows="2" label="Informação para conferência"
                v-model="analysisForm.inconsistency_reason" />

              <div class="payment-admin__section-title">Histórico</div>
              <div class="payment-admin__timeline">
                <div class="payment-admin__timeline-item">
                  <span class="payment-admin__timeline-dot" />
                  <div>
                    <strong>{{ details.submitted_by_client_user_id
                      ? "Comprovante inserido pela devedora"
                      : "Comprovante inserido pela Administração Judicial" }}</strong>
                    <small>{{ details.submitted_at | defaultDate("DD/MM/YYYY HH:mm") }}</small>
                  </div>
                </div>
                <div v-for="activity in details.activities || []" :key="activity.id"
                  class="payment-admin__timeline-item">
                  <span class="payment-admin__timeline-dot payment-admin__timeline-dot--muted" />
                  <div>
                    <strong>{{ activity.description }}</strong>
                    <small>{{ activity.created_at | defaultDate("DD/MM/YYYY HH:mm") }}<template v-if="activity.responsible_name"> · {{ activity.responsible_name }}</template></small>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
        <footer v-if="!loadingDetails && details.id" class="payment-admin__drawer-footer">
          <v-btn outlined color="primary" elevation="0" :loading="savingAnalysis"
            @click="updateAnalysisStatus('inconsistent')">Solicitar correção</v-btn>
          <v-btn outlined color="error" elevation="0" :loading="savingAnalysis"
            @click="updateAnalysisStatus('rejected')">Recusar</v-btn>
          <v-btn color="primary" elevation="0" :loading="savingAnalysis"
            @click="updateAnalysisStatus('validated')">Aprovar comprovante</v-btn>
        </footer>
      </div>
    </v-navigation-drawer>
  </div>
</template>

<script>
import axios from "axios";
import paymentProofStatuses from "@/common/data/payment-proof-status.data";
import paymentProofDocumentTypes from "@/common/data/payment-proof-document-types.data";

export default {
  name: "PaymentProofList",
  data: () => ({
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Comprovantes de pagamento", disabled: true, exact: true },
    ],
    dataset: {
      statuses: paymentProofStatuses,
      documentTypes: paymentProofDocumentTypes,
      paymentReasons: [
        { id: "pix", name: "Pix" },
        { id: "deposit", name: "Depósito em conta" },
        { id: "judicial_block", name: "Bloqueio judicial" },
        { id: "court_permit", name: "Alvará judicial" },
        { id: "agreement", name: "Acordo" },
        { id: "other", name: "Outro motivo" },
      ],
      payerTypes: [
        { id: "creditor", name: "Próprio credor" },
        { id: "recovering_company", name: "Recuperanda" },
        { id: "partner", name: "Sócio" },
        { id: "other_company", name: "Outra empresa" },
        { id: "other", name: "Outro terceiro" },
      ],
      yesNo: [
        { id: 1, name: "Sim" },
        { id: 0, name: "Não" },
      ],
    },
    showFilters: false,
    clients: [],
    createDialog: false,
    createSaving: false,
    createDragActive: false,
    createRules: {
      required: (value) => (value !== null && value !== undefined && value !== "") || "Campo obrigatório",
    },
    createForm: {
      client_id: null,
      creditor_registration: "",
      paid_amount: null,
      payment_date: "",
      payment_reason: null,
      payer_type: "creditor",
      payment_responsible: "",
      payer_registration: "",
      process_number: "",
      document_type: null,
      document: null,
    },
    filter: {
      query: "",
      status: null,
      pending: false,
      creditor_registration: "",
      process_number: "",
      payment_responsible: "",
      payer_registration: "",
      payer_type: null,
      is_third_party_payment: null,
      document_type: null,
      created_at_start: "",
      created_at_end: "",
      payment_date_start: "",
      payment_date_end: "",
    },
    summary: {
      total: 0,
      in_review: 0,
      validated: 0,
      rejected: 0,
      inconsistent: 0,
    },
    detailsDrawer: false,
    loadingDetails: false,
    savingAnalysis: false,
    details: {},
    analysisForm: {},
    selectedDocumentIndex: 0,
    headers: [
      { text: "Status", value: "status" },
      { text: "Credor", value: "creditor" },
      { text: "Processo", value: "process_number" },
      { text: "Valor", value: "paid_amount" },
      { text: "Pagamento", value: "payment_date" },
      { text: "Documento", value: "document_type" },
      { text: "Enviado em", value: "submitted_at" },
      { text: "", value: "actions", sortable: false, align: "end" },
    ],
    items: [],
    total: 0,
    loading: false,
    loadError: false,
    options: { page: 1, itemsPerPage: 10 },
    footerOptions: {
      itemsPerPageText: "Itens por página:",
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: "{0}-{1} de {2}",
    },
  }),
  computed: {
    drawerWidth() {
      return this.$vuetify.breakpoint.mdAndDown ? "100%" : 980;
    },
    selectedDocument() {
      const documents = this.details.documents || [];
      return documents[this.selectedDocumentIndex] || null;
    },
    detailFields() {
      if (!this.details.id) return [];
      return [
        { label: "CPF/CNPJ do credor", value: this.formatRegistration(this.details.creditor_registration) },
        { label: "Valor pago", value: this.formatCurrency(this.details.paid_amount) },
        { label: "Data do pagamento", value: this.formatDate(this.details.payment_date) },
        { label: "Processo relacionado", value: this.details.process_number || "Não informado" },
        { label: "Forma ou motivo", value: this.optionLabel(this.dataset.paymentReasons, this.details.payment_reason) },
        { label: "Tipo de pagador", value: this.optionLabel(this.dataset.payerTypes, this.details.payer_type) },
        { label: "Responsável", value: this.details.payment_responsible || "-" },
        { label: "Origem", value: this.submissionOrigin(this.details) },
      ];
    },
    summaryCards() {
      return [
        { key: "all", label: "Todos", value: this.summary.total, tone: "neutral", active: !this.filter.status && !this.filter.pending },
        { key: "pending", label: "Em análise", value: this.summary.in_review, tone: "info", active: this.filter.pending },
        { key: "validated", label: "Aprovados", value: this.summary.validated, tone: "success", active: this.filter.status === "validated" },
        { key: "rejected", label: "Recusados", value: this.summary.rejected, tone: "error", active: this.filter.status === "rejected" },
        { key: "inconsistent", label: "Inconsistências", value: this.summary.inconsistent, tone: "warning", active: this.filter.status === "inconsistent" },
      ];
    },
    activeFilters() {
      const labels = {
        status: "Situação",
        creditor_registration: "CPF/CNPJ do credor",
        process_number: "Processo",
        payment_responsible: "Responsável",
        payer_registration: "CPF/CNPJ do pagador",
        payer_type: "Tipo de pagador",
        is_third_party_payment: "Pagamento por terceiro",
        document_type: "Tipo de documento",
        created_at_start: "Enviado a partir de",
        created_at_end: "Enviado até",
        payment_date_start: "Pagamento a partir de",
        payment_date_end: "Pagamento até",
        pending: "Situação",
      };
      return Object.entries(this.filter)
        .filter(([key, value]) => key !== "query" &&
          (key !== "pending" || value === true) &&
          value !== "" && value !== null && value !== undefined)
        .map(([key, value]) => ({ key, label: `${labels[key] || key}: ${this.filterLabel(key, value)}` }));
    },
    activeFilterCount() {
      return this.activeFilters.length;
    },
  },
  watch: {
    options: {
      handler() {
        this.load();
      },
      deep: true,
    },
  },
  created() {
    this.loadClients();
    this.load();
  },
  methods: {
    async loadClients() {
      try {
        const response = await axios.get("/api/clients", { params: { start: 1, limit: 100000, search: {} } });
        this.clients = response.data.content.data || [];
      } catch (error) {
        this.$dialog.notify.error("Não foi possível carregar os casos.", { position: "top-right", timeout: 2000 });
      }
    },
    openCreateDialog() {
      this.resetCreateForm();
      this.createDialog = true;
    },
    closeCreateDialog() {
      if (this.createSaving) return;
      this.createDialog = false;
      this.resetCreateForm();
    },
    resetCreateForm() {
      this.createForm = {
        client_id: null,
        creditor_registration: "",
        paid_amount: null,
        payment_date: "",
        payment_reason: null,
        payer_type: "creditor",
        payment_responsible: "",
        payer_registration: "",
        process_number: "",
        document_type: null,
        document: null,
      };
      this.createDragActive = false;
      this.$nextTick(() => {
        if (this.$refs.createFormRef) this.$refs.createFormRef.resetValidation();
        if (this.$refs.createDocumentInput) this.$refs.createDocumentInput.value = "";
      });
    },
    openCreateDocumentPicker() {
      if (this.$refs.createDocumentInput) this.$refs.createDocumentInput.click();
    },
    onCreateDocumentSelected(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null;
      this.setCreateDocument(file);
    },
    onCreateDocumentDrop(event) {
      this.createDragActive = false;
      const file = event && event.dataTransfer && event.dataTransfer.files ? event.dataTransfer.files[0] : null;
      this.setCreateDocument(file);
    },
    setCreateDocument(file) {
      if (!file) return;
      const validType = ["application/pdf", "image/png", "image/jpeg"].includes(file.type) || /\.(pdf|png|jpe?g)$/i.test(file.name || "");
      if (!validType) {
        this.$dialog.notify.error("Selecione um arquivo PDF, PNG ou JPG.", { position: "top-right", timeout: 2500 });
        return;
      }
      if (Number(file.size || 0) > 20 * 1024 * 1024) {
        this.$dialog.notify.error("O arquivo deve possuir no máximo 20 MB.", { position: "top-right", timeout: 2500 });
        return;
      }
      this.$set(this.createForm, "document", file);
    },
    removeCreateDocument() {
      this.$set(this.createForm, "document", null);
      if (this.$refs.createDocumentInput) this.$refs.createDocumentInput.value = "";
    },
    formatFileSize(size) {
      const bytes = Number(size || 0);
      if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1).replace(".0", "")} MB`;
    },
    parseCurrencyAmount(value) {
      if (typeof value === "number") return value;
      return Number(String(value || "").replace(/[^\d,.-]/g, "").replace(/\./g, "").replace(",", "."));
    },
    positiveAmountRule(value) {
      return this.parseCurrencyAmount(value) > 0 || "Informe um valor maior que zero";
    },
    async submitCreateProof() {
      if (!this.$refs.createFormRef || !this.$refs.createFormRef.validate()) return;
      if (!this.createForm.document) {
        this.$dialog.notify.warning("Selecione o arquivo do comprovante.", { position: "top-right", timeout: 2000 });
        return;
      }

      this.createSaving = true;
      const payload = new FormData();
      payload.append("client_id", this.createForm.client_id);
      payload.append("creditor_registration", this.createForm.creditor_registration);
      payload.append("paid_amount", this.parseCurrencyAmount(this.createForm.paid_amount));
      payload.append("payment_date", this.createForm.payment_date);
      payload.append("payment_reason", this.createForm.payment_reason);
      payload.append("payment_responsible", this.createForm.payment_responsible);
      payload.append("payer_name", this.createForm.payment_responsible);
      payload.append("payer_type", this.createForm.payer_type);
      payload.append("document_type", this.createForm.document_type);
      payload.append("document", this.createForm.document);
      if (this.createForm.payer_type !== "creditor") {
        payload.append("payer_registration", this.createForm.payer_registration);
        payload.append("third_party_type", this.createForm.payer_type);
      }
      if (this.createForm.process_number) payload.append("process_number", this.createForm.process_number);

      try {
        const response = await axios.post("/api/admin/payment-proofs", payload);
        const proof = response.data.content || {};
        this.$dialog.notify[proof.has_inconsistency ? "warning" : "info"](
          proof.has_inconsistency ? "Comprovante salvo com inconsistência para análise." : "Comprovante salvo com sucesso.",
          { position: "top-right", timeout: 3000 }
        );
        this.createDialog = false;
        this.resetCreateForm();
        await this.load(true);
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2500 });
      } finally {
        this.createSaving = false;
      }
    },
    async load(resetPage = false) {
      if (resetPage) this.options.page = 1;
      this.loading = true;
      this.loadError = false;
      const search = Object.fromEntries(Object.entries(this.filter).filter(([, value]) => value !== "" && value !== null && value !== undefined));

      try {
        const result = await axios.get("/api/admin/payment-proofs", {
          params: { page: this.options.page, limit: this.options.itemsPerPage, search },
        });
        this.items = result.data.content.data;
        this.total = result.data.content.paging.total;
        this.summary = result.data.content.summary || this.summary;
      } catch (error) {
        this.loadError = true;
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      } finally {
        this.loading = false;
      }
    },
    applySummary(key) {
      this.$set(this.filter, "status", null);
      this.$set(this.filter, "pending", false);
      if (key === "pending") this.$set(this.filter, "pending", true);
      if (["validated", "rejected", "inconsistent"].includes(key)) this.$set(this.filter, "status", key);
      this.load(true);
    },
    applyStatusFilter() {
      this.$set(this.filter, "pending", false);
      this.load(true);
    },
    applyAdvancedFilters() {
      this.load(true);
    },
    clearFilters() {
      this.filter = {
        query: "",
        status: null,
        pending: false,
        creditor_registration: "",
        process_number: "",
        payment_responsible: "",
        payer_registration: "",
        payer_type: null,
        is_third_party_payment: null,
        document_type: null,
        created_at_start: "",
        created_at_end: "",
        payment_date_start: "",
        payment_date_end: "",
      };
      this.load(true);
    },
    removeFilter(key) {
      this.$set(this.filter, key, null);
      this.load(true);
    },
    filterLabel(key, value) {
      if (key === "status") {
        const item = this.dataset.statuses.find((status) => status.id === value);
        return item ? item.name : value;
      }
      if (key === "payer_type") {
        const item = this.dataset.payerTypes.find((type) => type.id === value);
        return item ? item.name : value;
      }
      if (key === "is_third_party_payment") return Number(value) ? "Sim" : "Não";
      if (key === "pending") return "Em análise";
      return value;
    },
    async viewItem(item) {
      this.detailsDrawer = true;
      this.loadingDetails = true;
      this.details = {};
      this.selectedDocumentIndex = 0;
      try {
        const result = await axios.get(`/api/admin/payment-proofs/${item.id}`);
        this.details = result.data.content;
        this.analysisForm = {
          status: this.details.status,
          internal_observation: this.details.internal_observation || "",
          inconsistency_reason: this.details.inconsistency_reason || "",
        };
      } catch (error) {
        this.detailsDrawer = false;
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      } finally {
        this.loadingDetails = false;
      }
    },
    async saveAnalysis() {
      if (!this.details.id) return;
      this.savingAnalysis = true;
      try {
        await axios.patch(`/api/admin/payment-proofs/${this.details.id}/status`, {
          status: this.analysisForm.status,
          internal_observation: this.analysisForm.internal_observation,
        });
        await axios.post(`/api/admin/payment-proofs/${this.details.id}/observations`, {
          internal_observation: this.analysisForm.internal_observation,
          inconsistency_reason: this.analysisForm.inconsistency_reason,
        });
        this.$dialog.notify.info("Análise salva com sucesso.", { position: "top-right", timeout: 2000 });
        await this.viewItem({ id: this.details.id });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      } finally {
        this.savingAnalysis = false;
      }
    },
    async updateAnalysisStatus(status) {
      this.analysisForm.status = status;
      await this.saveAnalysis();
    },
    exportCsv() {
      const headings = ["Status", "Credor", "CPF/CNPJ", "Processo", "Valor", "Pagamento", "Documento", "Enviado em"];
      const rows = this.items.map((item) => [
        this.optionLabel(this.dataset.statuses, item.status),
        item.creditor_name || item.name || "",
        item.creditor_registration || "",
        item.process_number || "",
        item.paid_amount || "",
        this.formatDate(item.payment_date),
        this.optionLabel(this.dataset.documentTypes, item.document_type),
        item.submitted_at || "",
      ]);
      const csv = [headings, ...rows]
        .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(";"))
        .join("\n");
      const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "comprovantes-recebidos.csv";
      link.click();
      URL.revokeObjectURL(url);
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return value || "-";
    },
    formatDate(value) {
      if (!value) return "Não informado";
      const parts = String(value).slice(0, 10).split("-");
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
    },
    optionLabel(options, value) {
      const item = options.find((option) => option.id === value);
      return item ? item.name : value || "-";
    },
    submissionOrigin(item) {
      if (!item.submitted_by_client_user_id) return "Administração Judicial";
      return item.payment_proof_import_id ? "Devedora · importação em lote" : "Devedora · envio individual";
    },
    isImageDocument(document) {
      return String(document.document_mime || "").startsWith("image/");
    },
    isPdfDocument(document) {
      return String(document.document_mime || "").includes("pdf") ||
        String(document.document_name || "").toLowerCase().endsWith(".pdf");
    },
    statusColor(status) {
      return {
        submitted: "primary",
        in_review: "info",
        inconsistent: "warning",
        validated: "success",
        rejected: "error",
      }[status] || "grey";
    },
  },
};
</script>

<style scoped>
.payment-admin {
  max-width: 1320px;
}

.payment-admin__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.payment-admin__title {
  color: #101828;
  font-size: 28px;
  line-height: 1.25;
}

.payment-admin__subtitle {
  margin: 6px 0 0;
  color: #667085;
}

.payment-admin__header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.payment-admin__card {
  overflow: hidden;
  border-color: #e4e7ec !important;
  border-radius: 10px;
}

.payment-admin__summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.payment-admin__summary-card {
  display: flex;
  min-height: 82px;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 8px;
  padding: 13px 15px;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fff;
  color: #475467;
  text-align: left;
}

.payment-admin__summary-card--active {
  border-color: #c7d0f2;
  box-shadow: 0 1px 4px rgba(42, 48, 112, 0.12);
}

.payment-admin__summary-card strong {
  color: #101828;
  font-size: 22px;
  line-height: 1;
}

.payment-admin__summary-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.payment-admin__summary-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.payment-admin__card-title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
}

.payment-admin__toolbar {
  display: grid;
  grid-template-columns: minmax(300px, 1fr) 220px auto;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #e4e7ec;
  background: #fafbfc;
}

.payment-admin__filter-count {
  display: inline-flex;
  min-width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  margin-left: 7px;
  padding: 0 6px;
  border-radius: 10px;
  background: #2a3070;
  color: #fff;
  font-size: 11px;
}

.payment-admin__creditor {
  display: flex;
  min-width: 170px;
  flex-direction: column;
  gap: 2px;
}

.payment-admin__creditor span {
  color: #667085;
  font-size: 12px;
}

.payment-admin__active-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid #eef0f3;
}

.payment-admin__empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #667085;
}

.payment-admin__filter-drawer ::v-deep .v-navigation-drawer__content,
.payment-admin__details-drawer ::v-deep .v-navigation-drawer__content {
  overflow: hidden;
}

.payment-admin__filter-shell,
.payment-admin__drawer-shell {
  display: flex;
  height: 100vh;
  flex-direction: column;
  background: #fff;
}

.payment-admin__filter-header,
.payment-admin__drawer-title {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 22px;
  border-bottom: 1px solid #e4e7ec;
}

.payment-admin__filter-header h2,
.payment-admin__drawer-title h2 {
  margin: 0;
  color: #101828;
  font-size: 19px;
  line-height: 1.3;
}

.payment-admin__filter-header p,
.payment-admin__drawer-title p {
  margin: 4px 0 0;
  color: #667085;
  font-size: 12px;
}

.payment-admin__close {
  width: 32px !important;
  min-width: 32px !important;
  height: 32px !important;
  flex: 0 0 32px;
  border-color: #d0d5dd;
  border-radius: 7px;
}

.payment-admin__filter-form {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
}

.payment-admin__filter-body,
.payment-admin__drawer-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 18px 22px 24px;
}

.payment-admin__filter-section {
  margin: 7px 0 8px;
  padding-bottom: 7px;
  border-bottom: 1px solid #e4e7ec;
  color: #344054;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.payment-admin__filter-footer,
.payment-admin__drawer-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  padding: 14px 22px;
  border-top: 1px solid #e4e7ec;
  background: #fff;
  box-shadow: 0 -5px 18px rgba(16, 24, 40, 0.04);
}

.payment-admin__drawer-heading {
  display: flex;
  align-items: center;
  gap: 9px;
}

.payment-admin__review-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(340px, 0.88fr);
  gap: 18px;
}

.payment-admin__document-panel,
.payment-admin__information-panel {
  min-width: 0;
  padding: 15px;
  border: 1px solid #e4e7ec;
  border-radius: 9px;
  background: #fff;
}

.payment-admin__panel-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.payment-admin__panel-heading > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.payment-admin__panel-heading strong {
  color: #20263b;
  font-size: 14px;
}

.payment-admin__panel-heading span {
  overflow: hidden;
  color: #667085;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-admin__detail-list {
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}

.payment-admin__detail-list > div {
  display: grid;
  grid-template-columns: minmax(130px, 0.8fr) minmax(0, 1.2fr);
  gap: 12px;
  padding: 9px 11px;
  border-bottom: 1px solid #eef0f3;
}

.payment-admin__detail-list > div:last-child {
  border-bottom: 0;
}

.payment-admin__detail-list > div:nth-child(odd) {
  background: #fafbfc;
}

.payment-admin__detail-list span {
  color: #667085;
  font-size: 11px;
}

.payment-admin__detail-list strong {
  color: #344054;
  font-size: 11.5px;
  text-align: right;
}

.payment-admin__detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
}

.payment-admin__detail-grid > div {
  display: flex;
  min-height: 68px;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-bottom: 1px solid #eef0f3;
}

.payment-admin__detail-grid > div:nth-child(odd) {
  border-right: 1px solid #eef0f3;
  background: #fafbfc;
}

.payment-admin__detail-grid span,
.payment-admin__timeline small {
  color: #667085;
  font-size: 12px;
}

.payment-admin__section-title {
  margin: 24px 0 12px;
  color: #101828;
  font-size: 16px;
  font-weight: 600;
}

.payment-admin__documents {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
}

.payment-admin__documents button {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 7px;
  padding: 10px;
  border: 1px solid #e4e7ec;
  border-radius: 7px;
  background: #fff;
  color: #344054;
}

.payment-admin__documents button.active {
  border-color: #2a3070;
  background: #f7f8fc;
}

.payment-admin__documents button span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-admin__preview {
  overflow: hidden;
  height: calc(100vh - 270px);
  min-height: 430px;
  margin-top: 0;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #f7f8fa;
}

.payment-admin__preview img,
.payment-admin__preview iframe {
  width: 100%;
  height: 100%;
  border: 0;
  object-fit: contain;
}

.payment-admin__preview-empty {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #667085;
}

.payment-admin__timeline {
  padding-left: 7px;
}

.payment-admin__timeline-item {
  position: relative;
  display: flex;
  gap: 12px;
  padding-bottom: 20px;
}

.payment-admin__timeline-item:not(:last-child)::before {
  position: absolute;
  top: 12px;
  bottom: 0;
  left: 5px;
  width: 1px;
  background: #e4e7ec;
  content: "";
}

.payment-admin__timeline-item > div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.payment-admin__timeline-dot {
  z-index: 1;
  width: 11px;
  height: 11px;
  flex: 0 0 11px;
  margin-top: 5px;
  border-radius: 50%;
  background: #2a3070;
}

.payment-admin__timeline-dot--muted {
  background: #98a2b3;
}

.payment-proof-create-card {
  overflow: hidden;
  border-radius: 12px !important;
}

.payment-proof-create-header {
  display: flex;
  min-height: 74px;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px !important;
  border-bottom: 1px solid #e5e7ed;
  letter-spacing: 0;
  text-transform: none;
}

.payment-proof-create-header > div,
.payment-proof-section-header > div {
  display: flex;
  flex-direction: column;
}

.payment-proof-create-header > div > span {
  color: #172033;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.payment-proof-create-header small,
.payment-proof-section-header small {
  margin-top: 3px;
  color: #737b8c;
  font-size: 10px;
  font-weight: 400;
}

.payment-proof-create-body {
  display: grid;
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  padding: 16px !important;
  background: #f8f9fb;
}

.payment-proof-create-body form {
  display: grid;
  gap: 14px;
}

.payment-proof-form-section {
  overflow: hidden;
  border: 1px solid #e1e5eb;
  border-radius: 10px;
  background: #fff;
}

.payment-proof-section-header {
  display: flex;
  min-height: 60px;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-bottom: 1px solid #e9ebf0;
}

.payment-proof-section-header > span {
  display: flex;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: #eef2f6;
  color: #31536f;
}

.payment-proof-section-header strong {
  color: #303446;
  font-size: 12px;
}

.payment-proof-form-grid {
  margin: -5px !important;
  padding: 14px;
}

.payment-proof-form-grid > .col {
  padding: 5px;
}

.payment-proof-form-grid .op-field > label {
  display: block;
  margin: 0 0 6px 1px;
  color: #343747;
  font-size: 10px;
  font-weight: 650;
}

.payment-proof-form-grid .op-field label em {
  color: #dc2626;
  font-style: normal;
}

.payment-proof-form-grid .op-field label span {
  margin-left: 4px;
  color: #959aaa;
  font-size: 8px;
  font-weight: 500;
  text-transform: uppercase;
}

.payment-proof-form-grid ::v-deep .v-input__slot {
  min-height: 44px !important;
  border-radius: 6px !important;
}

.payment-proof-native-file {
  display: none;
}

.payment-proof-dropzone {
  display: flex;
  min-height: 140px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 6px;
  margin: 14px;
  padding: 20px;
  border: 1px dashed #b8c2cf;
  border-radius: 9px;
  outline: none;
  background: #fbfcfd;
  cursor: pointer;
  text-align: center;
  transition: background-color .15s ease, border-color .15s ease, box-shadow .15s ease;
}

.payment-proof-dropzone:hover,
.payment-proof-dropzone:focus,
.payment-proof-dropzone.dragging {
  border-color: #315f81;
  background: #f3f8fc;
  box-shadow: 0 0 0 3px rgba(49, 95, 129, .1);
}

.payment-proof-dropzone-icon {
  display: flex;
  width: 48px;
  height: 48px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eaf0f5;
  color: #315f81;
}

.payment-proof-dropzone > div {
  display: flex;
  flex-direction: column;
}

.payment-proof-dropzone strong,
.payment-proof-selected-file strong {
  color: #303446;
  font-size: 11px;
}

.payment-proof-dropzone small,
.payment-proof-selected-file small {
  margin-top: 2px;
  color: #777d8e;
  font-size: 9px;
}

.payment-proof-file-rule {
  color: #989dac;
  font-size: 8px;
  text-transform: uppercase;
}

.payment-proof-selected-file {
  display: grid;
  align-items: center;
  gap: 11px;
  grid-template-columns: 42px minmax(0, 1fr) 32px;
  margin: 14px;
  padding: 12px;
  border: 1px solid #cfe2d6;
  border-radius: 9px;
  background: #f7faf8;
}

.payment-proof-selected-file > span {
  display: flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #e3f1e8;
  color: #28744a;
}

.payment-proof-selected-file > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.payment-proof-selected-file strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payment-proof-create-actions {
  min-height: 64px;
  gap: 8px;
  padding: 12px 18px !important;
  border-top: 1px solid #e5e7ed;
  background: #fff;
}

.payment-proof-create-actions .v-btn {
  height: 36px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

@media (max-width: 800px) {
  .payment-admin__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .payment-admin__header-actions {
    width: 100%;
    flex-wrap: wrap;
  }

  .payment-admin__toolbar {
    grid-template-columns: 1fr;
  }

  .payment-admin__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .payment-admin__detail-grid,
  .payment-admin__documents {
    grid-template-columns: 1fr;
  }

  .payment-admin__review-layout {
    grid-template-columns: 1fr;
  }

  .payment-admin__preview {
    height: 480px;
    min-height: 0;
  }

  .payment-proof-create-body {
    max-height: calc(100vh - 180px);
  }
}
</style>
